import React, { useState, useCallback, useEffect, useRef } from "react";
import Map, {
  Marker,
  NavigationControl,
  GeolocateControl,
} from "react-map-gl/mapbox";
import "mapbox-gl/dist/mapbox-gl.css";
import { MapPin, Search, X } from "lucide-react";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import { useTheme } from "../../context/ThemeContext";

const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN || "";

const LocationPicker = ({
  value,
  onChange,
  placeholder = "Select location on map",
}) => {
  const { theme } = useTheme();
  const [isExpanded, setIsExpanded] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);

  const searchTimeoutRef = useRef(null);
  const ignoreNextSearchRef = useRef(false);

  const [viewState, setViewState] = useState({
    latitude: value?.lat || 34.0522,
    longitude: value?.lng || -118.2437,
    zoom: value ? 14 : 10,
  });

  const [marker, setMarker] = useState(value || null);

  const isValidToken =
    MAPBOX_TOKEN &&
    !MAPBOX_TOKEN.includes("replace_with_your_token") &&
    MAPBOX_TOKEN.length > 20;

  // Shared logic used by Map Click and Geolocate button
  const handleLocationUpdate = useCallback((lat, lng) => {
    const newLocation = {
      lat,
      lng,
      address: `${lat.toFixed(4)}, ${lng.toFixed(4)}`,
    };
    setMarker(newLocation);

    if (isValidToken) {
      fetch(
        `https://api.mapbox.com/geocoding/v5/mapbox.places/${lng},${lat}.json?access_token=${MAPBOX_TOKEN}`
      )
        .then((res) => res.json())
        .then((data) => {
          if (data.features && data.features.length > 0) {
            const address = data.features[0].place_name;
            const updatedLocation = { ...newLocation, address };
            setMarker(updatedLocation);
            onChange?.(updatedLocation);
          } else {
            onChange?.(newLocation);
          }
        })
        .catch(() => onChange?.(newLocation));
    } else {
      onChange?.(newLocation);
    }
  }, [onChange, isValidToken]);

  const handleMapClick = useCallback(
    (event) => {
      const { lngLat } = event;
      handleLocationUpdate(lngLat.lat, lngLat.lng);
    },
    [handleLocationUpdate]
  );

  // Search function
  const handleSearch = useCallback(async () => {
    if (!searchQuery.trim() || !isValidToken) return;

    setIsSearching(true);
    try {
      const response = await fetch(
        `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(
          searchQuery
        )}.json?access_token=${MAPBOX_TOKEN}&limit=5`
      );
      const data = await response.json();
      setSearchResults(data.features || []);
    } catch (error) {
      console.error("Search error:", error);
      setSearchResults([]);
    }
    setIsSearching(false);
  }, [searchQuery, isValidToken]);

  useEffect(() => {
    if (ignoreNextSearchRef.current) {
      ignoreNextSearchRef.current = false;
      return;
    }

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (searchQuery.trim().length > 2) {
      searchTimeoutRef.current = setTimeout(() => {
        handleSearch();
      }, 600);
    } else if (searchQuery.trim().length === 0) {
      setSearchResults([]);
    }

    return () => clearTimeout(searchTimeoutRef.current);
  }, [searchQuery, handleSearch]);

  // Select search result
  const selectSearchResult = useCallback(
    (result) => {
      const [lng, lat] = result.center;
      const newLocation = {
        lat,
        lng,
        address: result.place_name,
      };

      ignoreNextSearchRef.current = true;

      setMarker(newLocation);
      setViewState((prev) => ({
        ...prev,
        latitude: lat,
        longitude: lng,
        zoom: 14,
      }));

      setSearchResults([]);
      setSearchQuery(result.place_name);
      onChange?.(newLocation);
    },
    [onChange]
  );

  const handleGeolocate = useCallback((pos) => {
    const { latitude, longitude } = pos.coords;
    handleLocationUpdate(latitude, longitude);
  }, [handleLocationUpdate]);

  const clearSelection = useCallback(() => {
    setMarker(null);
    setSearchQuery("");
    setSearchResults([]);
    onChange?.(null);
  }, [onChange]);

  const confirmLocation = useCallback(() => {
    setIsExpanded(false);
  }, []);

  if (!isExpanded) {
    return (
      <div
        onClick={() => setIsExpanded(true)}
        className="w-full border border-input rounded-lg p-3 cursor-pointer hover:border-primary/50 hover:bg-accent/50 transition-all group bg-background"
      >
        <div className="flex items-center gap-3">
          <div
            className={`p-2 rounded-lg ${
              marker
                ? "bg-green-100 dark:bg-green-900/30"
                : "bg-muted group-hover:bg-indigo-100 dark:group-hover:bg-indigo-900/30"
            } transition-colors`}
          >
            <MapPin
              size={18}
              className={
                marker
                  ? "text-green-600 dark:text-green-400"
                  : "text-muted-foreground group-hover:text-indigo-500 dark:group-hover:text-indigo-400"
              }
            />
          </div>
          <div className="flex-1 min-w-0">
            {marker ? (
              <>
                <p className="text-sm font-medium text-foreground truncate">
                  {marker.address}
                </p>
                <p className="text-xs text-muted-foreground">
                  Click to change location
                </p>
              </>
            ) : (
              <>
                <p className="text-sm text-muted-foreground">{placeholder}</p>
                <p className="text-xs text-muted-foreground/70">
                  Click to open map
                </p>
              </>
            )}
          </div>
          {marker && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                clearSelection();
              }}
              className="p-1 hover:bg-accent rounded-full transition-colors"
            >
              <X size={16} className="text-muted-foreground" />
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="w-full border border-border rounded-xl overflow-hidden bg-card shadow-lg">
      {/* Search Bar */}
      <div className="p-3 border-b border-border bg-muted/50">
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground w-4 h-4" />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  handleSearch();
                }
              }}
              placeholder="Search for a location..."
              className="pl-9 pr-3 bg-background"
            />
          </div>
          <Button
            type="button"
            onClick={handleSearch}
            disabled={isSearching}
            className="bg-primary hover:bg-primary/90 text-primary-foreground"
          >
            {isSearching ? "Searching..." : "Search"}
          </Button>
        </div>

        {/* Search Results Dropdown */}
        {searchResults.length > 0 && (
          <div className="mt-2 bg-popover border border-border rounded-lg shadow-sm max-h-40 overflow-y-auto">
            {searchResults.map((result) => (
              <button
                key={result.id}
                type="button"
                onClick={() => selectSearchResult(result)}
                className="w-full text-left px-3 py-2 hover:bg-accent hover:text-accent-foreground border-b border-border last:border-0 transition-colors"
              >
                <p className="text-sm font-medium text-foreground truncate">
                  {result.text}
                </p>
                <p className="text-xs text-muted-foreground truncate">
                  {result.place_name}
                </p>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Map Section */}
      <div className="h-64 relative">
        {isValidToken ? (
          <Map
            {...viewState}
            onMove={(evt) => setViewState(evt.viewState)}
            onClick={handleMapClick}
            style={{ width: "100%", height: "100%" }}
            mapStyle={
              theme === "dark"
                ? "mapbox://styles/mapbox/dark-v11"
                : "mapbox://styles/mapbox/streets-v12"
            }
            mapboxAccessToken={MAPBOX_TOKEN}
            cursor="crosshair"
          >
            <NavigationControl position="top-right" showCompass={false} />
            <GeolocateControl
                position="top-right"
                onGeolocate={handleGeolocate}
                trackUserLocation={false}
            />

            {marker && (
              <Marker latitude={marker.lat} longitude={marker.lng}>
                <div className="relative">
                  <div className="absolute -top-1 -left-1 w-6 h-6 bg-primary rounded-full animate-ping opacity-30" />
                  <div className="relative bg-primary p-1.5 rounded-full shadow-lg border-2 border-white dark:border-zinc-900">
                    <MapPin size={16} className="text-primary-foreground" />
                  </div>
                </div>
              </Marker>
            )}
          </Map>
        ) : (
          <div className="w-full h-full flex flex-col items-center justify-center bg-muted text-muted-foreground">
            <MapPin size={32} className="mb-2 opacity-30" />
            <p className="text-sm">Map requires Mapbox token</p>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="p-3 border-t border-border bg-muted/50 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 text-sm text-muted-foreground min-w-0 flex-1">
          {marker ? (
            <>
              <MapPin size={14} className="text-green-600 dark:text-green-400 flex-shrink-0" />
              <span className="truncate cursor-help" title={marker.address}>
                {marker.address}
              </span>
            </>
          ) : (
            <span className="truncate">No location selected</span>
          )}
        </div>

        <div className="flex items-center gap-2 flex-shrink-0">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setIsExpanded(false)}
            className="h-8 px-3"
          >
            Cancel
          </Button>
          <Button
            type="button"
            size="sm"
            onClick={confirmLocation}
            disabled={!marker}
            className="bg-primary hover:bg-primary/90 text-primary-foreground h-8 px-3"
          >
            Confirm
          </Button>
        </div>
      </div>
    </div>
  );
};

export default LocationPicker;
